import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import type {
    FormElementOption,
    FormElementVariableData,
    FormWidgetElement,
    FormChatBotData,
} from '@sites/entity-core';

import type { FormControlType, FormElement } from '@common/types/form-element.type';
import type { AddressBookVariable } from '@integrations/address-books';
import type { CrmDeal } from '@integrations/crm-deals';
import type { CrmContact } from '@integrations/crm-contacts';
import type { TranslationsType } from '@common/constants/form-element-label.const';
import { controlsLabels } from '@common/constants/form-element-label.const';
import {
    EAttributeTypesCrm,
    EContactFieldsCrm,
    ECrmCommentPlace,
    EFormControlType,
    EFormInputType,
    EHtmlInputType,
    ESocialNetworks,
    EVariablesTypes,
} from '@common/enums';
import { defaultFormElements } from '@common/constants/form-element.const';
import { FormElementService } from '@common/services/form-element.service';
import { DeviceMode } from '@common/types/layout.type';

import { SpCommonEditorComponent } from '../sp-common-editor/src/sp-editor-common.component';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpSwitchComponent } from '../../ui/sp-switch/src/sp-switch.component';

/** Payload for the chat-bot variable mapping emitted up to the host. */
export type ChatBotVariableChange = {
    socialNetwork: ESocialNetworks;
    variable: FormElementVariableData | null;
};

/**
 * Signal-form editor for one form element — the signal twin of the
 * FormGroup-based `sp-form-element-settings`. Mirrors that template
 * (type dropdown, label/placeholder, dynamic phone mask, hidden value,
 * textarea CRM comment place, address-book / CRM contact / CRM deal variable
 * selects with the select-options cascade, chat-bot variable selects,
 * radio/select options editor, required switch) with the value-in
 * (`FormWidgetElement` from `@sites/entity-core`) / `valueChangeEvent`-out
 * contract.
 *
 * Differences from the original forced by the value contract:
 *  - object-valued selects (`[ngValue]`) become index/key-based natives;
 *  - `changeBotVariable` no longer mutates the parent's chatBots FormArray —
 *    it emits {@link chatBotVariableChange} so the host updates its array.
 */
@Component({
    selector: 'sp-form-element-settings-signal',
    templateUrl: './sp-form-element-settings-signal.component.html',
    styleUrls: ['./sp-form-element-settings-signal.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, BsDropdownModule, TooltipModule, SpCommonEditorComponent, SpSwitchComponent],
})
export class SpFormElementSettingsSignalComponent {
    private readonly translate = inject(TranslateService);
    private readonly formElementService = inject(FormElementService);

    public readonly element = input<FormWidgetElement | null>(null);
    public readonly elements = input<FormWidgetElement[]>([]);
    public readonly selectedChatBots = input<FormChatBotData[]>([]);
    public readonly addChatBotToForm = input<boolean>(false);
    public readonly isInitVariables = input<boolean>(false);
    public readonly variablesOptions = input<FormElementVariableData[][]>([]);

    public readonly addressBookVariablesList = input<AddressBookVariable[]>([]);
    public readonly crmContactsVariablesList = input<CrmContact[]>([]);
    public readonly crmDealsVariablesList = input<CrmDeal[]>([]);

    public readonly filteredFormControls = input<FormControlType[]>([]);

    public readonly isFormControlDisabled = input<boolean>(false);
    public readonly isCrmDealsListDisabled = input<boolean>(false);
    public readonly isCrmContactListDisabled = input<boolean>(false);
    public readonly isAddressBookListDisabled = input<boolean>(false);
    public readonly isShowProtectedList = input<boolean>(true);
    /** Drops `file` from the element-type dropdown — login-edu forms take no uploads. */
    public readonly isFileTypeDisabled = input<boolean>(false);
    public readonly isPaymentEduForm = input<boolean>(false);
    public readonly isLoginEduForm = input<boolean>(false);

    public readonly baseHref = input<string>('');
    public readonly deviceMode = input<DeviceMode>(DeviceMode.desktop);

    public readonly valueChangeEvent = output<FormWidgetElement>();
    public readonly chatBotVariableChange = output<ChatBotVariableChange>();

    public readonly formInputType = EFormInputType;
    public readonly formControlType = EFormControlType;
    public readonly controlsLabels = controlsLabels as TranslationsType;
    public readonly variableTypes = EVariablesTypes;
    public readonly crmCommentPlaceArray = [
        ECrmCommentPlace.crmAllComment,
        ECrmCommentPlace.crmContactComment,
        ECrmCommentPlace.crmDealComment,
    ];

    private readonly model = signal<FormWidgetElement | null>(null);

    // --- Model field reads ---

    public readonly type = computed(() => this.model()?.type);
    public readonly label = computed(() => this.model()?.label ?? '');
    public readonly placeholder = computed(() => this.model()?.placeholder ?? '');
    public readonly variable = computed(() => this.model()?.variable ?? null);
    public readonly crmContactVariable = computed(() => this.model()?.crmContactVariable ?? null);
    public readonly crmDealVariable = computed(() => this.model()?.crmDealVariable ?? null);
    public readonly crmCommentPlace = computed(() => this.model()?.crmCommentPlace ?? null);
    public readonly required = computed(() => Boolean(this.model()?.required));
    public readonly isProtected = computed(() => Boolean(this.model()?.protected));
    public readonly isDynamicPlaceholderEnabled = computed(() => Boolean(this.model()?.isDynamicPlaceholderEnabled));
    public readonly hiddenValue = computed(() => (this.model()?.value as string) ?? '');
    public readonly options = computed<FormElementOption[]>(() => this.model()?.options ?? []);

    // --- Type predicates (value-based equivalents of the FormGroup type guards) ---

    public readonly isEmailType = computed(() => this.type() === EFormInputType.email);
    public readonly isPhoneType = computed(() => this.type() === EFormInputType.phone);
    public readonly isPasswordType = computed(() => this.type() === EFormInputType.password);
    public readonly isHiddenType = computed(() => this.type() === EFormInputType.hidden);
    public readonly isCheckboxType = computed(() => this.type() === EFormControlType.checkbox);
    public readonly isRadioType = computed(() => this.type() === EFormControlType.radio);
    public readonly isSelectType = computed(() => this.type() === EFormControlType.select);
    public readonly isTextareaType = computed(() => this.type() === EFormControlType.textarea);
    public readonly isFileType = computed(() => this.type() === EFormInputType.file);
    public readonly isDateType = computed(() => this.type() === EFormInputType.date);
    /** Inputs (everything except checkbox/radio/select/textarea). */
    public readonly isInputLike = computed(
        () => !this.isCheckboxType() && !this.isRadioType() && !this.isSelectType() && !this.isTextareaType()
    );
    /** System elements carry a protected system variable (email/phone/gdpr…). */
    public readonly isSystemElement = computed(() => Boolean(this.model()?.protected));

    public readonly isHasEmail = computed(() => this.elements().some((el) => el.type === EFormInputType.email));
    public readonly isHasPhone = computed(() => this.elements().some((el) => el.type === EFormInputType.phone));
    /** A form takes at most one file field — and none at all where uploads are off. */
    public readonly isHasFile = computed(
        () => this.isFileTypeDisabled() || this.elements().some((el) => el.type === EFormInputType.file)
    );

    public readonly isShowDropdown = computed(
        () => !this.isProtected() || (!this.isEmailType() && !this.isPasswordType())
    );
    public readonly isShowDefaultList = computed(
        () =>
            (this.isEmailType() && this.isHasPhone()) ||
            (this.isPhoneType() && this.isHasEmail()) ||
            !this.isProtected()
    );

    public readonly showMakeRequiredPredicate = computed(
        () =>
            !this.isEmailType() &&
            !this.isPasswordType() &&
            !this.isPhoneType() &&
            !this.isHiddenType() &&
            !this.isCrmDealVariableSelect()
    );

    public readonly disableFilteredContactFields = computed(
        () => this.type() === EFormInputType.date || this.type() === EFormInputType.number
    );

    // --- Variable lists (filtered against already-used variables) ---

    public readonly defaultElements = computed<Map<string, FormElement>>(() => {
        const map = new Map(defaultFormElements);
        map.delete(EFormInputType.question);

        if (this.isLoginEduForm()) {
            map.delete(EFormControlType.textarea);
        }

        this.filteredFormControls().forEach((element) => {
            map.delete(element);
        });

        return map;
    });

    public readonly defaultProtectedElements = computed<Map<string, FormElement>>(
        () =>
            new Map(
                [...defaultFormElements].filter(
                    ([, v]) => v.type === EFormInputType.email || v.type === EFormInputType.phone
                )
            )
    );

    public readonly filteredContactFields = computed(() => {
        const usedFields = this.elements().map((element) => element.crmContactVariable);
        return Object.values(EContactFieldsCrm).filter((field) => !usedFields.includes(field));
    });

    public readonly filteredCrmContactsVariables = computed<CrmContact[]>(() => {
        const used = new Set(this.elements().map((element) => element.crmContactVariable));
        return this.crmContactsVariablesList().filter((field) => !used.has(field.id));
    });

    public readonly filteredCrmDealsVariables = computed<CrmDeal[]>(() => {
        const used = new Set(this.elements().map((element) => element.crmDealVariable));
        return this.crmDealsVariablesList().filter((variable) => !used.has(variable.id));
    });

    public readonly filteredAddressBookVariables = computed<AddressBookVariable[]>(() => {
        const used = this.elements().map((element) => element.variable);
        return this.addressBookVariablesList().filter((variable) => !used.includes(variable.variable_name));
    });

    public readonly isCrmDealVariableSelect = computed(() => {
        const selected = this.getCrmDealVariableById(this.crmDealVariable() as number);
        if (!selected) return false;
        return selected.type === EAttributeTypesCrm.select || selected.type === EAttributeTypesCrm.multiselect;
    });

    public readonly isCrmContactVariableSelect = computed(() => {
        const selected = this.getCrmContactVariableById(this.crmContactVariable() as number);
        if (!selected) return false;
        return selected.type === EAttributeTypesCrm.select || selected.type === EAttributeTypesCrm.multiselect;
    });

    /** True when neither CRM variable is mapped — the atLeastOne error of the FormGroup era. */
    public readonly hasCrmVariableError = computed(
        () =>
            !this.isSystemElement() &&
            !this.isTextareaType() &&
            this.crmContactVariable() == null &&
            this.crmDealVariable() == null
    );

    constructor() {
        effect(() => {
            const incoming = this.element();
            if (!incoming) return;
            this.model.set(incoming);
        });
    }

    // --- Type switching ---

    public changeElementType(type: FormControlType | string): void {
        const element = this.formElementService.getDefaultElement(type) as unknown as FormWidgetElement;
        this.model.set(element);
        this.valueChangeEvent.emit(element);
    }

    // --- Scalar setters ---

    public onLabelChange(event: Event): void {
        this.patch({ label: (event.target as HTMLInputElement).value });
    }

    public saveLabel(label: string): void {
        this.patch({ label });
    }

    public onPlaceholderChange(event: Event): void {
        this.patch({ placeholder: (event.target as HTMLInputElement).value });
    }

    public onHiddenValueChange(event: Event): void {
        this.patch({ value: (event.target as HTMLInputElement).value });
    }

    public setDynamicPhonePlaceholder(value: boolean): void {
        this.patch({ isDynamicPlaceholderEnabled: value });
    }

    public toggleRequired(value: boolean): void {
        if (this.isProtected()) {
            return;
        }
        this.patch({ required: value });
    }

    public onCrmCommentPlaceChange(event: Event): void {
        this.patch({
            crmCommentPlace: (event.target as HTMLSelectElement).value as FormWidgetElement['crmCommentPlace'],
        });
    }

    public onAddressBookVariableChange(event: Event): void {
        this.patch({ variable: (event.target as HTMLSelectElement).value || null });
    }

    // --- CRM variable selects (string select value → enum string or numeric id) ---

    public onCrmContactVariableChange(event: Event): void {
        const raw = (event.target as HTMLSelectElement).value;
        const parsed = this.parseCrmVariableValue(raw);

        const selectedType = this.crmContactsVariablesList().find((variable) => variable.id === parsed)?.type;
        if (selectedType === EAttributeTypesCrm.select || selectedType === EAttributeTypesCrm.multiselect) {
            // Select-typed CRM attribute drives the element options; deal
            // variable is mutually exclusive. Mirrors the FormGroup cascade
            // (`changeCrmContactVariable` + `setCrmContactSelectOptions`).
            const variable = this.getCrmContactVariableById(parsed as number);
            const base = this.formElementService.getDefaultElement(
                selectedType === EAttributeTypesCrm.multiselect ? 'multiselect' : EFormControlType.select
            ) as unknown as FormWidgetElement;
            const next: FormWidgetElement = {
                ...base,
                crmContactVariable: parsed,
                crmDealVariable: null,
                options: (variable?.options ?? []).map((option: string, index: number) => ({
                    value: option,
                    label: option,
                    selected: index === 0,
                })),
            };
            this.model.set(next);
            this.valueChangeEvent.emit(next);
            return;
        }

        this.patch({ crmContactVariable: parsed });
    }

    public onCrmDealVariableChange(event: Event): void {
        const raw = (event.target as HTMLSelectElement).value;
        const parsed = this.parseCrmVariableValue(raw);

        const selectedType = this.crmDealsVariablesList().find((variable) => variable.id === parsed)?.type;
        if (selectedType === EAttributeTypesCrm.select || selectedType === EAttributeTypesCrm.multiselect) {
            const variable = this.getCrmDealVariableById(parsed as number);
            const base = this.formElementService.getDefaultElement(
                selectedType === EAttributeTypesCrm.multiselect ? 'multiselect' : EFormControlType.select
            ) as unknown as FormWidgetElement;
            const next: FormWidgetElement = {
                ...base,
                crmDealVariable: parsed,
                crmContactVariable: null,
                options: (variable?.options ?? []).map((option: string, index: number) => ({
                    value: option,
                    label: option,
                    selected: index === 0,
                })),
            };
            this.model.set(next);
            this.valueChangeEvent.emit(next);
            return;
        }

        this.patch({ crmDealVariable: raw === '' ? null : parsed });
    }

    private parseCrmVariableValue(raw: string): number | string | null {
        if (raw === '' || raw === 'null') return null;
        const numeric = Number(raw);
        return Number.isNaN(numeric) ? raw : numeric;
    }

    // --- Chat-bot variable mapping ---

    public filteredBotVariables(
        sn: ESocialNetworks,
        botVariables: FormElementVariableData[]
    ): FormElementVariableData[] {
        const key = this.messengerVariableKey(sn);
        if (!key) return botVariables ?? [];

        const userVariables = this.elements().map((element) => element[key]);
        const selectedUserVariable = this.model()?.[key] ?? null;
        const userNames = new Set(userVariables?.map((el) => el?.name));

        return botVariables
            ? [...botVariables.filter((variable) => !userNames.has(variable.name)), selectedUserVariable].filter(
                  Boolean
              )
            : [];
    }

    public selectedBotVariable(sn: ESocialNetworks): FormElementVariableData | null {
        const key = this.messengerVariableKey(sn);
        return key ? (this.model()?.[key] ?? null) : null;
    }

    public onBotVariableChange(chatBot: FormChatBotData, event: Event, options: FormElementVariableData[]): void {
        const index = Number((event.target as HTMLSelectElement).value);
        const variable = options[index] ?? null;

        const key = this.messengerVariableKey(chatBot.socialNetwork as ESocialNetworks);
        if (key) {
            this.patch({ [key]: variable } as Partial<FormWidgetElement>);
        }

        this.chatBotVariableChange.emit({ socialNetwork: chatBot.socialNetwork as ESocialNetworks, variable });
    }

    private messengerVariableKey(
        sn: ESocialNetworks
    ):
        | 'telegramVariable'
        | 'instagramVariable'
        | 'viberVariable'
        | 'messengerVariable'
        | 'whatsappVariable'
        | 'tiktokVariable'
        | null {
        switch (sn) {
            case ESocialNetworks.telegram:
                return 'telegramVariable';
            case ESocialNetworks.instagram:
                return 'instagramVariable';
            case ESocialNetworks.viber:
                return 'viberVariable';
            case ESocialNetworks.messenger:
                return 'messengerVariable';
            case ESocialNetworks.whatsapp:
                return 'whatsappVariable';
            case ESocialNetworks.tiktok:
                return 'tiktokVariable';
            default:
                return null;
        }
    }

    // --- Options editor (radio/select) ---

    public addOption(): void {
        const options = this.options();
        const index = options.length;
        const label = this.translate.instant('form_radio_option');
        const value = this.translate.instant('form_radio_value');

        this.patch({
            options: [...options, { label: `${label} ${index}`, value: `${value} ${index}`, selected: false }],
        });
    }

    public removeOption(index: number, event: Event): void {
        event.stopPropagation();

        let next = this.options().filter((_, i) => i !== index);
        if (next.length && !next.some((option) => option.selected)) {
            next = next.map((option, i) => (i === 0 ? { ...option, selected: true } : option));
        }

        this.patch({ options: next });
    }

    public updateOptionLabel(index: number, event: Event): void {
        const label = (event.target as HTMLInputElement).value;
        this.patch({ options: this.options().map((option, i) => (i === index ? { ...option, label } : option)) });
    }

    public updateOptionValue(index: number, event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.patch({ options: this.options().map((option, i) => (i === index ? { ...option, value } : option)) });
    }

    public checkRadioOption(index: number): void {
        const options = this.options();
        const next = options[index].selected
            ? options.map((option, i) => (i === index ? { ...option, selected: false } : option))
            : options.map((option, i) => ({ ...option, selected: i === index }));
        this.patch({ options: next });
    }

    public checkSelectOption(index: number): void {
        const options = this.options();
        if (this.model()?.htmlInputType === EHtmlInputType.multiselect) {
            this.patch({
                options: options.map((option, i) => (i === index ? { ...option, selected: !option.selected } : option)),
            });
            return;
        }
        this.checkRadioOption(index);
    }

    // --- Helpers ---

    public getVariableName(id: string | number, type: EVariablesTypes): string {
        switch (type) {
            case EVariablesTypes.crmContactAttribute:
                return this.crmContactsVariablesList().find((variable) => variable.id === id)?.name || id.toString();
            case EVariablesTypes.crmDealAttribute:
                return this.crmDealsVariablesList().find((variable) => variable.id === id)?.name;
            case EVariablesTypes.emailAddressBook:
                return (
                    this.addressBookVariablesList().find((variable) => variable.variable_name === id)?.variable_name ||
                    id.toString()
                );
            default:
                return id?.toString() ?? '';
        }
    }

    public disableCrmSelectVariable(variable: CrmContact): boolean {
        if (this.type() !== 'select' && variable.type === EAttributeTypesCrm.multiselect) {
            return true;
        }
        if (this.type() === EFormInputType.date && variable.type !== EAttributeTypesCrm.date) {
            return true;
        }
        if (this.type() !== EFormInputType.date && variable.type === EAttributeTypesCrm.date) {
            return true;
        }
        if (this.type() === EFormInputType.number && variable.type !== EAttributeTypesCrm.number) {
            return true;
        }
        if (
            this.type() === EFormControlType.checkbox &&
            variable.type !== EAttributeTypesCrm.checkbox &&
            variable.type !== EAttributeTypesCrm.text
        ) {
            return true;
        }
        if (variable.type !== EAttributeTypesCrm.select) {
            return false;
        }
        return !(this.isRadioType() || this.isSelectType());
    }

    public disableAddressBookVariable(variable: AddressBookVariable): boolean {
        return this.type() !== EFormInputType.date && variable.variable_type === EHtmlInputType.date;
    }

    private getCrmDealVariableById(id: CrmDeal['id']): CrmDeal | undefined {
        return this.crmDealsVariablesList().find((variable) => variable.id === id);
    }

    private getCrmContactVariableById(id: CrmContact['id']): CrmContact | undefined {
        return this.crmContactsVariablesList().find((variable) => variable.id === id);
    }

    private patch(patch: Partial<FormWidgetElement>): void {
        const current = this.model();
        if (!current) return;
        const next = { ...current, ...patch };
        this.model.set(next);
        this.valueChangeEvent.emit(next);
    }
}
